// ev-chart
import type { ComputedRef, CSSProperties, Ref } from 'vue';
import type { Dayjs } from 'dayjs';
import z from 'zod';
import type { SortType } from '@/postgresql/components/top10SqlListWindow/top10SqlListWindow.types';
import type { OrderBy as SortingOrderBy } from '@/mysql/components/mysqlTop10SortingListWindow/uses';
import type { NotificationInfo } from '@/config/components/alertNotificationInfo/alertNotificationInfo.types';
import type {
  CurrentV7Request,
  DataTrigger,
  MetricV7Request,
  MetricV7RequestInterval,
  MetricV7RequestPeriod,
} from '@/api-client/data/model';
import type { SlowItemV7 } from '@/api-client/mysql/model';
import type {
  FilterItem,
  FilterSearchSpecificFilterFnKey,
  CustomFilterOption,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import { DB_TYPE, PA_TOGGLE_DATA_TYPE, ALERT_PLATFORM_TYPES } from '@/common/define/common.define';
import { RoleId } from '@/common/auth/auth.defines';
import type { IconType } from '@/types/icon.types';
import type { GridRawRowDataType } from '@/common/components/molecules/modernGrid/modernGrid.types';
import type { TooltipPosition } from '@/common/components/molecules/simpleTextTooltip/simpleTextTooltip.setup';
import type { WidgetModeType, WidgetChartType } from '@/dashboard/shared/types/widgets.types';
import type { OrderBy } from '@/postgresql/components/top10ObjectListWindow/top10ObjectListWindow.types';

export type UUID = string;

export type WidgetUUID = UUID;
export type FilterUUID = UUID;

export type WidgetCustomId = string;
export type FilterCustomId = string;

export interface SeriesData {
  id?: string;
  show?: boolean;
  /** 식별/매칭용 raw name. 표시는 alias 우선이며 widget adapter/setup에서 1회 변환한다. */
  name?: string;
  /** 표시용 alias 후보. 식별/매칭에는 사용하지 않는다. */
  alias?: string;
  label?: string;
  type?: string;
  color?: string | any;
  showValue?: {
    // bar only
    use?: boolean;
    fontColor?: string;
    fontSize?: number;
    align?: string;
    textColor?: string;
    decimalPoint?: number;
    // EVUI bar 차트는 셀 값과 함께 두 번째 인자로 { label, index }(라벨값·라벨 인덱스)를 넘긴다.
    formatter?: (value: any, meta?: { label?: any; index?: number }) => string;
  };
  showLegend?: boolean;
  lineWidth?: number; // line only
  fill?: boolean; // line only
  fillOpacity?: number; // line only
  fillColor?: string; // line only
  pointHighlight?: boolean; // line only
  point?: boolean; // line only
  pointSize?: number; // line, scatter only
  pointStyle?: string; // line, scatter only
  pointFill?: string; // line, scatter only
  combo?: boolean; // combo only
  overflowColor?: string; // scatter > scatter only;
  passingValue?: number | null; // line, stack line, area, stack area only;
  thickness?: number; // bar only
  segments?: number[]; // line only
  interpolation?: 'none' | 'linear' | 'zero';
}

type ChartType = 'timeDayjs' | 'others';
type DataId = string;
type SeriesKey = 'aiUpper' | 'aiLower' | ({} & DataId);
type ChartDataSeries<Mode = 'normal'> = Mode extends 'AI'
  ? Record<SeriesKey, SeriesData>
  : Record<DataId, SeriesData>;
export interface ChartData<T extends ChartType = 'others', Mode = 'normal'> {
  series: ChartDataSeries<Mode>;
  groups?: string[][];
  labels?: T extends 'timeDayjs' ? Dayjs[] : any;
  data?: {
    [key: string]: any;
  };
}

// PlotLine / PlotBand 공통 라벨 옵션
export interface PlotLabel {
  show?: boolean;
  // 라벨 텍스트(=alias). showValue:false 면 이 값을 그대로 표시
  text?: string | null;
  fontSize?: number;
  maxWidth?: number;
  fontColor?: string;
  fillColor?: string;
  // 숫자(400) 또는 CSS 키워드('bold' 등)
  fontWeight?: number | string;
  fontFamily?: string;
  lineColor?: string;
  // 박스 테두리 선 굵기 (기본 0)
  lineWidth?: number;
  // stroke와 label 사이의 간격 (기본값: 폰트사이즈에 비례, 폰트사이즈 12px 기준 5px 정도)
  gap?: number;
  // true → "{text} {value}" 합성 (value 는 해당 축 formatter 적용)
  showValue?: boolean;
  // showValue:true 일 때 value 포맷 override. 없으면 축 formatter 사용(축과 다른 정밀도/단위로 표시할 때)
  valueFormatter?: (value: number) => string | number;
  // 가로 배치(Y축 기준): plot 밖 우측 / 안쪽 좌 / 안쪽 우
  position?: 'outside' | 'innerStart' | 'innerEnd';
  // 라벨 박스 모서리 반경(px)
  borderRadius?: number;
  // 박스 안쪽 여백. 숫자 또는 { top, right, bottom, left }. null 이면 fontSize/4
  padding?: number | { top?: number; right?: number; bottom?: number; left?: number } | null;
  // 말풍선 꼬리 (color 가 null 이면 박스 배경색 fillColor 사용)
  pointer?: {
    show?: boolean;
    color?: string | null;
  };
  // plot 너비 기준 단계 축약 (둘 다 null 이면 항상 풀 표시)
  responsive?: {
    // plot 너비 < 값 → value 만 표시
    valueOnlyBelow?: number | null;
    // plot 너비 < 값 → 라벨 미노출(본체 유지)
    hideBelow?: number | null;
  };
  // value-only 상태에서 hover 시 text tooltip (데스크탑 전용)
  showTextOnHover?: {
    use?: boolean;
    backgroundColor?: string;
    fontColor?: string;
    fontWeight?: number | string;
    borderColor?: string | null;
    borderRadius?: number;
    fontSize?: number;
    fontFamily?: string;
    useShadow?: boolean;
    shadowOpacity?: number;
    padding?: { top?: number; right?: number; bottom?: number; left?: number };
  };
  // 배치 정렬 (밴드/세로축 라벨에서 사용)
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  textOverflow?: 'none' | 'ellipsis';
}

export interface PlotLines {
  color?: string;
  value: null | number | Dayjs;
  segments?: number[];
  lineWidth?: number;
  label?: PlotLabel;
}

export interface PlotBands {
  color?: string;
  from: null | number | Dayjs;
  to: null | number | Dayjs;
  label?: PlotLabel;
  // start/end 모서리 stroke
  border?: {
    color?: string;
    width?: number;
    // dash 간격(점선). 예) [2, 2]
    segments?: number[];
  } | null;
}
export interface Scrollbar {
  use: boolean;
  width?: number;
  height?: number;
  background?: string;
  showButton?: boolean;
  thumbStyle?: {
    background?: string;
    radius?: number;
  };
  resetPosition?: boolean;
}

export type TimeAxesTypeInterval =
  | 'millisecond'
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year';

export interface Axes {
  type?: 'linear' | 'time' | 'log' | 'step'; // line, scatter -> time only
  autoScaleRatio?: number | null;
  startToZero?: boolean;
  showAxis?: boolean;
  axisLineColor?: string;
  showGrid?: boolean;
  gridLineColor?: string;
  showIndicator?: boolean;
  timeFormat?: string;
  categoryMode?: boolean;
  range?:
    | [min: number, max: number]
    /** dayjs 가 파싱 가능한 시간 문자열 (예: 'YYYY-MM-DD HH:mm:ss', ISO 8601) */
    | [minString: string, maxString: string]
    | Ref<[min: number, max: number]>
    | Dayjs[]
    | ComputedRef<Dayjs[]>
    | ((min: number, max: number, maxSteps?: number) => [min: number, max: number])
    | null;
  interval?: string | number | Ref<string> | { time: number; unit: string } | TimeAxesTypeInterval;
  niceScale?: boolean;
  horizontal?: boolean;
  labelStyle?: {
    show?: boolean;
    fontSize?: number | string;
    color?: string;
    fontFamily?: string;
    fontWeight?: number;
    fitWidth?: boolean;
    fitDir?: string;
    padding?: number;
    maxWidth?: number | string;
    alignToGridLine?: boolean;
    fixWidth?: number;
  };
  showLastLabel?: boolean;
  lastLabelFontStyle?: {
    fontSize?: number | string;
    color?: string;
    fontFamily?: string;
    fontWeight?: number;
  };
  firstLabelFontStyle?: {
    fontSize?: number | string;
    color?: string;
    fontFamily?: string;
    fontWeight?: number;
  };
  formatter?: (value: number | string, data: { prev: number | null }) => string | number;
  plotLines?: PlotLines[];
  plotBands?: PlotBands[];
  scrollbar?: Scrollbar;
  flow?: boolean;
  decimalPoint?: number | 'auto';
  scaleChange?: boolean;
  fixedSteps?: boolean;
}

interface Title {
  show?: boolean;
  height?: number;
  text?: string;
  style?: CSSProperties;
}

interface LegendTitleColumn {
  use?: boolean;
  title?: string;
  style?: CSSProperties;
  decimalPoint?: number;
  unit?: string;
}

interface LegendTable {
  use?: boolean;
  style?: {
    row?: CSSProperties;
    header?: CSSProperties;
  };
  columns?: {
    name?: Omit<LegendTitleColumn, 'use' | 'decimalPoint' | 'unit'>;
    min?: Omit<LegendTitleColumn, 'decimalPoint'> & { formatter?: (v: any) => any };
    max?: LegendTitleColumn;
    avg?: LegendTitleColumn;
    total?: LegendTitleColumn;
    last?: LegendTitleColumn;
  };
}

interface Legend {
  show?: boolean;
  position?: string;
  color?: string;
  inactive?: string;
  width?: number;
  height?: number;
  padding?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  allowResize?: boolean;
  stopClickEvt?: boolean; // only pie
  table?: LegendTable;
  virtualScroll?: boolean;
  clickMode?: 'active' | 'inactive'; // default: active
  external?: boolean; // default: false, 외부 범례 사용 여부
}
type Value = number | string;
type Label = string;
export interface Tooltip {
  use?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  useShadow?: boolean;
  shadowOpacity?: number;
  throttledMove?: boolean;
  debouncedHide?: boolean;
  sortByValue?: boolean;
  useScrollbar?: boolean;
  htmlScrollTarget?: string;
  showHeader?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  scrollbar?: {
    use?: boolean;
    maxSeriesCount?: number;
  };
  formatter?:
    | ((any: {
        x: Value | Label;
        y: Value | Label;
        o: Value;
        name: string;
        seriesId: string;
      }) => string)
    | {
        title?: (titleArg: any) => string;
        label?: (labelArg: {
          seriesId: string;
          seriesName: string;
          itemData: any;
          dataId: string;
        }) => string;
        value?: (valueArg: any) => string;
        customValue?: (valueArg: any) => string;
        html?: (
          valueArg: {
            color: string;
            data: any;
            name: string;
            sId: string;
            dataId?: string;
            index?: number;
          }[],
        ) => string;
      };
  showAllValueInRange?: boolean;
  fontSize?: { title?: number; contents?: number };
  fontColor?:
    | string
    | { title?: string; label?: string; value?: string }
    | {
        label?: (labelArg: { id: string; name: string; value: any; dataId: string }) => string;
        value?: (valueArg: { id: string; name: string; value: any; dataId: string }) => string;
      };
  colorShape?: 'rect' | 'circle';
  rowPadding?: { top?: number; bottom?: number; left?: number; right?: number };
  virtualScroll?: {
    use?: 'auto' | boolean; // default: 'auto'
    estimatedRowHeight?: number; // default: 28
    threshold?: number; // default: 50,
    overscan?: number; // default: 5,
  };
}

interface Indicator {
  use?: boolean;
  color?: string;
  segments?: ReadonlyArray<number>;
}

interface MaxTip {
  use?: boolean;
  formatter?: (value: number | string) => string;
  fixedPosTop?: boolean;
  showIndicator?: boolean;
  indicatorColor?: string;
  tipBackground?: string;
  tipTextColor?: string;
  tipStyle: {
    height?: number;
    background?: string;
    textColor?: string;
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: number;
  };
}

interface BorderStyle {
  color?: string;
  lineWidth?: number;
  opacity?: number;
  radius?: number;
}

interface SelectItem {
  use?: boolean;
  limit?: number;
  showTextTip?: boolean;
  tipText?: 'value' | 'label';
  showTip?: boolean;
  showIndicator?: boolean;
  fixedPosTop?: boolean;
  useApproximateValue?: boolean;
  indicatorColor?: string;
  tipBackground?: string;
  tipTextColor?: string;
  useClick?: boolean;
  useSeriesOpacity?: boolean;
  useDeselectItem?: boolean;
  useDeselectOverflow?: boolean;
  useLabelOpacity?: boolean;
  useBorder?: boolean;
  borderStyle?: BorderStyle;
  tipStyle?: {
    background?: string;
    textColor?: string;
    fontWeight?: number;
    fontSize?: number;
  };
}

interface TipStyle {
  textColor?: string;
  fontWeight?: number;
  fontSize?: number;
}

export interface SelectLabel {
  use?: boolean;
  useClick?: boolean;
  limit?: number;
  useDeselectOverflow?: boolean;
  showTip?: boolean;
  showTextTip?: true;
  tipText?: string;
  tipStyle?: TipStyle;
  showIndicator?: boolean;
  indicatorColor?: string;
  useSeriesOpacity?: boolean;
  useLabelOpacity?: boolean;
  fixedPosTop?: boolean;
  useApproximateValue?: boolean;
  tipBackground?: string;
  useBothAxis?: boolean;
}

export interface Padding {
  top?: number;
  right?: number;
  left?: number;
  bottom?: number;
}

interface ColorsByRange {
  color: string;
  label: string;
}

interface Stroke {
  show?: boolean;
  color?: string;
  lineWidth?: number;
  opacity?: number;
  radius?: number;
}
interface Overlapping {
  use?: boolean;
}

// chart annotations
interface AnnotationContentContext {
  xValue: number | string | null;
  yValue: number | string | null;
  seriesId: string | null;
  seriesName: string | null;
  dataIndex: number;
  percentage: number | null;
}

interface AnnotationPositionPixel {
  type: 'pixel';
  x?: number;
  y?: number;
  offsetX?: number;
  offsetY?: number;
}

interface AnnotationPositionAxis {
  type: 'axis';
  xAxisIndex?: number;
  yAxisIndex?: number;
  xValue?: number | string | null;
  yValue?: number | string | null;
  offsetX?: number;
  offsetY?: number;
}

interface AnnotationPositionSeries {
  type: 'series';
  seriesId?: string | null;
  location?: 'start' | 'end' | number;
  offsetX?: number;
  offsetY?: number;
}

type AnnotationPosition =
  | AnnotationPositionPixel
  | AnnotationPositionAxis
  | AnnotationPositionSeries;

interface AnnotationConnector {
  enabled?: boolean;
  type?: 'straight' | 'elbow';
  style?: {
    stroke?: string;
    strokeWidth?: number;
    dashStyle?: 'solid' | 'dash' | 'dot';
  };
}

interface AnnotationStyle {
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  padding?: number | number[];
  fontSize?: string | number;
  fontWeight?: string;
  fontFamily?: string;
  textAlign?: string;
  // callout only
  anchor?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  arrowSize?: number;
  // circle only
  radius?: number;
}

export interface Annotation {
  id?: string;
  type: 'text' | 'badge' | 'callout' | 'circle';
  content?: string | ((ctx: AnnotationContentContext) => string) | null;
  position?: AnnotationPosition;
  connector?: AnnotationConnector;
  style?: AnnotationStyle;
}

// chart: bar, line, scatter, pie
export interface ChartOption {
  type?: 'bar' | 'pie' | 'line' | 'scatter' | 'heatMap';
  width?: string | number;
  height?: string | number;
  thickness?: number | `${number}px`;
  cPadRatio?: number;
  borderRadius?: number;
  horizontal?: boolean;
  axesX?: Axes[]; // bar, line, scatter only
  axesY?: Axes[]; // bar, line, scatter only
  title?: Title;
  legend?: Legend;
  tooltip?: Tooltip; // bar , line only
  overlapping?: Overlapping;
  indicator?: Indicator; // bar, line, scatter only
  maxTip?: MaxTip; // bar, line only
  selectItem?: SelectItem; // bar, line only
  padding?: Padding;
  selectLabel?: SelectLabel;
  doughnutHoleSize?: number;
  sunburst?: boolean;
  reverse?: boolean;
  selectSeries?: {
    use?: boolean;
    useClick?: boolean;
    limit?: number;
    useDeselectOverflow?: boolean;
  };
  dragSelection?: {
    use?: boolean;
    keepDisplay?: boolean;
    startArea?: string;
    displayFromStartArea?: boolean;
  };
  plot?: {
    aboveSeries?: boolean;
  };
  /**
   * 차트 영역(축 범위)을 벗어난 데이터의 클램프 여부.
   * `true` 시 범위 밖 데이터를 축 경계로 클램프하지 않는다(= 경계에 잘못된 위치로 마커를 그리지 않고 무시·숨김).
   * @default false
   */
  displayOverflow?: boolean;
  heatMapColor?: {
    min?: string;
    max?: string;
    rangeCount?: number;
    colorsByRange?: ColorsByRange[];
    stroke?: Stroke;
    error?: string;
    decimalPoint?: number;
  };
  realTimeScatter?: {
    use?: boolean;
    range?: number;
  };
  pieStroke?: {
    use?: boolean;
    color?: string;
    lineWidth?: number;
  };
  seriesReverse?: boolean;
  coordinateDedupe?: boolean;
  eventBehavior?: {
    legendClick?: 'emitOnly' | 'update';
  };
  shallowDataWatch?: boolean;
  shallowOptionsWatch?: boolean;
  workerRender?: boolean;
  annotations?: Annotation[];
}

interface Toolbar {
  show?: boolean;
  items?: {
    [key: string]: {
      icon?: string;
      size?: string;
      title?: string;
    };
  };
}

export interface ChartGroupOption {
  zoom?: {
    keepZoomStatus?: boolean;
    useAnimation?: boolean;
    useWheelMove?: boolean;
    bufferMemoryCnt?: number;
    toolbar?: Toolbar;
  };
}

export interface GroupSelectedLabel {
  dataIndex: number[];
  selectedDate?: string | Dayjs;
}

interface Selection {
  fillColor?: string;
  opacity?: number;
}

export interface ChartBrushOption {
  show?: boolean;
  chartIdx?: number;
  height?: number;
  useDebounce?: boolean;
  showLabel?: boolean;
  selection?: Selection;
  useWheelMove?: boolean;
}

export interface ChartDragSelectInfo {
  data: {
    seriesName: string;
    seriesId: string;
    item: any[];
  }[];
  e?: MouseEvent;
  range: {
    xMax: number;
    xMin: number;
    yMax: number;
    yMin: number;
  };
}

export type GridColumnType =
  | 'string'
  | 'number'
  | 'float'
  | 'boolean'
  | 'stringNumber'
  | 'string[]'
  | 'age'
  | '';
export type ExtendedGridDataType = GridColumnType | 'object' | 'datetime';

// ev-grid
export interface GridColumn<Field = string> {
  caption: string;
  field: Field;
  type?: ExtendedGridDataType;
  width?: number;
  searchable?: boolean;
  sortable?: boolean;
  fixed?: boolean;
  hide?: boolean;
  decimal?: number;
  summaryType?: 'sum' | 'average' | 'max' | 'min' | 'count';
  summaryRenderer?: string;
  summaryData?: string[];
  expandColumn?: boolean;
  align?: string;
  hiddenDisplay?: boolean;
  sortOption?: {
    sortType?: string;
  };
  columnIndex?: number[];
  autoVisible?: boolean;
  disabledHide?: boolean;
  customHeader?: boolean;
  defaultSearchField?: boolean;
  ignoreNameSize?: boolean;
}

export interface GridSelectedLabel {
  dataIndex: number[];
}

interface CustomColumnAttrs {
  useOriginTime?: boolean; // not converting with timezone
  useOriginNotFormattedTime?: boolean; // not converting with timezone and format (ex. 2025-08-22T00:00:00Z => 2025-08-22T00:00:00)
  toggle?: boolean; // toggle sum <-> avg
  unit?: string; // ms, sec, min, hour, ...
  excelCaption?: string; // caption used on excel export
  conditionalClickable?: {
    use?: boolean;
    predicate?: ({ rowObj }: { rowObj: any }) => boolean;
  };
  dataSet?: any[];
  incDecIndicator?: boolean; // render increase / decrease indicator
  tooltipDescription?: string; // trend-chart-cell 툴팁 하단에 표시할 설명 문구
}

export interface CustomColumn<Field = string> extends GridColumn<Field> {
  index?: number;
  unit?: {
    dataType: string;
    value: string; // ex. KB, MB, %
  };
  rendererType?:
    | 'ratio'
    | 'label'
    | 'capitalize-label'
    | 'upper-label'
    | 'chip-cell'
    | 'multi-chip-cell'
    | 'chip-cell-clickable'
    | 'use-html-chip-cell'
    | 'color-ball-cell'
    | 'action'
    | 'trend-chart-cell'
    | 'tooltip-label'
    | 'sec' // ms => sec
    | 'toSec' // not ms => sec
    | 'byte' // byte => KB, MB, GiB, etc.
    | 'digit'
    | 'deltaSymbolNumber'
    | 'input-cell';
  clickable?: boolean;
  attrs?: CustomColumnAttrs;
  formatter?: (row) => any;
  filterType?: 'onlyRaw' | 'onlyFormatted' | 'both';
  customHeader?: boolean;
  customHeaderSort?: boolean; // customHeader 컬럼에 기본 정렬 버튼 렌더링
  sortingFn?: (rowA: any, rowB: any, columnId: string) => number;
  enableResizing?: boolean;
  specificFilterFnKey?: FilterSearchSpecificFilterFnKey;
  defaultSearchField?: boolean; // 기본 검색 컬럼 여부
  useQuickFilter?: boolean;
  excludeFromColumnList?: boolean;
  ignoreNameSize?: boolean; // 이름 기반 사이즈 매칭 무시
  children?: CustomColumn[];
}

export type CustomColumnsReadonly<Field = string> = readonly Readonly<CustomColumn<Field>>[];

type NonReadonly<T> = {
  -readonly [K in keyof T]: T[K];
};
export type ExtractedCustomColumnTypes<T extends CustomColumn[] | CustomColumnsReadonly> =
  NonReadonly<{
    [K in keyof T]: T[K]['type'] extends 'string'
      ? string
      : T[K]['type'] extends 'number'
        ? number
        : T[K]['type'] extends 'float'
          ? number
          : T[K]['type'] extends 'stringNumber'
            ? string | number
            : T[K]['type'] extends 'boolean'
              ? boolean
              : T[K]['type'] extends 'string[]'
                ? string[]
                : T[K]['type'] extends 'object'
                  ? object
                  : T[K]['type'] extends 'datetime'
                    ? Date
                    : unknown;
  }>;

export type ColumnWithHideInfo = Omit<CustomColumn, 'hiddenDisplay'> & {
  index: number;
  hiddenDisplay: boolean;
};

export type RowData<ColumnData extends CustomColumn[] | Readonly<CustomColumn[]>> =
  ExtractedCustomColumnTypes<ColumnData>;

type ExtractColumnType<T extends CustomColumn, K extends string> = T['field'] extends K
  ? T['type'] extends 'boolean'
    ? boolean
    : T['type'] extends 'number'
      ? number
      : T['type'] extends 'string'
        ? string
        : T['type'] extends 'stringNumber'
          ? string | number
          : unknown
  : unknown;

type ExtractColumnByField<
  T extends CustomColumn[] | CustomColumnsReadonly,
  K extends string,
> = Extract<T[number], { field: K }>;

export type ExtractedCustomColumnTreeData<
  T extends CustomColumn[] | CustomColumnsReadonly,
  ExtraData = {},
> = {
  [K in T[number]['field']]: ExtractColumnType<ExtractColumnByField<T, K>, K>;
} & ExtraData & {
    expand?: boolean;
    show?: boolean;
    children?: ExtractedCustomColumnTreeData<NonReadonly<T>, ExtraData>[];
  };

export type ExtractedCustomColumnData<T extends CustomColumn[] | CustomColumnsReadonly> = {
  [K in T[number]['field']]: ExtractColumnType<ExtractColumnByField<T, K>, K>;
};

export interface ClickedRowInfo<RowDataType> {
  cellIndex: string;
  cellName: string;
  rowData: RowDataType;
  rowIndex: number;
  event: MouseEvent;
}

export interface DblClickedRowInfo<RowDataType> {
  cellIndex: string;
  cellName: string;
  rowData: RowDataType;
  rowIndex: number;
  event: MouseEvent;
  bgColor?: string;
}

export interface ClickedCellInfo<RowDataType> {
  event: MouseEvent;
  field: string;
  value: RowDataType;
  row: RowDataType[];
  cellIndex: number;
}

export interface ClickedCellInfoForTransformed<RowDataType extends GridRawRowDataType> {
  event: MouseEvent;
  field: keyof RowDataType;
  value: any;
  row: RowDataType;
  cellIndex: number;
  cellName?: string;
  rowIndex?: number;
  rowId?: string;
}

export interface ClickedCellAndFieldInfoForTransformed<RowDataType extends GridRawRowDataType> {
  evt: MouseEvent;
  rowIndex: number;
  chips: string[];
  totalChips: string[];
  fieldName?: keyof RowDataType;
  rowData?: RowDataType;
  columnIndex?: number;
  isClickable: boolean;
}

interface DefaultElColumn {
  prop?: string;
  label: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  resizable?: boolean;
  rendererType?: 'ratio' | 'label' | 'chip-cell' | 'action';
}
export interface ElColumn extends DefaultElColumn {
  children?: DefaultElColumn[];
}

export interface GridContextMenuParams {
  click?: (param: any) => void;
  contextmenuInfo?: any[];
  selectedRow?: any[];
  text: any;
}

export interface MenuItem {
  text?: string | number;
  iconClass?: string;
  itemClass?: string;
  disabled?: boolean;
  hidden?: boolean;
  children?: MenuItem[];
  click?: (param: GridContextMenuParams) => void;
}

type ColumnMenuKeys = 'ascending' | 'descending' | 'filter' | 'hide';

interface ColumnMenuHidden extends Partial<Record<ColumnMenuKeys, boolean>> {}

interface ColumnMenuText extends Partial<Record<ColumnMenuKeys, string>> {}

export interface GridSearchBarOption {
  use: boolean;
  mode?: 'full' | 'right' | 'left';
  placeholder?: string;
  useClientFilter?: boolean;
  useMergedToolbar?: boolean;
  filterSearch?: {
    use: boolean;
    storageKey?: string;
    filterItems?: FilterItem[];
    placeholder?: string;
    customOption?: CustomFilterOption[];
    initialValues?: {
      logicalOperator: 'AND' | 'OR';
      tokens: Array<{
        key: string;
        operator: string;
        value: string;
      }>;
    };
    useAutoValueItems?: boolean;
    defaultActive?: boolean;
    persistActiveState?: boolean;
  };
}
export interface GridOption {
  adjust?: boolean;
  showHeader?: boolean;
  rowHeight?: number;
  rowMinHeight?: number;
  columnWidth?: number;
  scrollWidth?: number;
  useFilter?: boolean;
  useExportExcel?: {
    use?: boolean;
    title?: string;
    icon?: string;
  };
  useGridSetting?: {
    use?: boolean;
    useDefaultColumnSetting?: boolean;
    customContextMenu?: MenuItem[];
    columnMenuText?: string;
    searchText?: string;
    emptyText?: string;
    okBtnText?: string;
    toolbar?: {
      disableMinHeight?: boolean;
      settingIconBottom?: number | string;
    };
    onAfterResetUserEnv?: () => void;
    // 트리 그리드 전용 — true 시 설정 메뉴에 전체 펼치기/접기 항목 자동 추가
    useExpandCollapseMenu?: boolean;
  };
  maintainScrollOnUpdateRows?: boolean;
  customAscFunction?: Record<string, (a: any, b: any) => number>;
  useCheckbox?: {
    use?: boolean;
    mode?: 'multi' | 'single' | 'each' | 'parentOnly';
    headerCheck?: boolean;
  };
  useSelection?: {
    use?: boolean;
    multiple?: boolean;
    limitCount?: number;
    disableDeselect?: boolean; // EVUI 에서 제공하지 않고 <base-grid> 에서 구현
    disableRootNodeSelect?: boolean;
    disableCellClickSelect?: boolean;
  };
  style?: {
    stripe?: boolean;
    border?: 'all' | 'rows' | 'columns' | 'none';
    highlight?: number;
  };
  customContextMenu?: MenuItem[];
  customContextMenuClass?: string;
  hiddenColumnMenuItem?: ColumnMenuHidden;
  columnMenuText?: ColumnMenuText;
  count?: boolean;
  searchValue?: string;
  useSummary?: boolean;
  page?: {
    use: boolean;
    useClient?: boolean;
    perPage?: number;
    currentPage?: number;
    total?: number;
    isInfinite?: boolean;
    showPageInfo?: boolean;
  };
  rowDetail?: {
    use?: boolean;
    detailRowHeight?: number;
  };
  emptyText?: string;
  /** 원본 rows가 있는데 검색/필터 결과가 0건일 때 표시할 문구. 미지정 시 기본 "검색 결과가 없습니다." — ''로 억제 가능 */
  searchEmptyText?: string;
  useSort?: ComputedRef<boolean> | boolean;
}

export interface SelectboxItem<T = string | number | boolean> {
  name: string;
  value: T;
  disabled?: boolean;
  iconClass?: string;
}

export type SelectboxItemWithDefault<T = string | number | boolean> = SelectboxItem<T> & {
  isDefault?: boolean;
};

export type TabPanel<T = string | number | null> = {
  text: string | number | null;
  value: T;
  iconClass?: string;
  component?: any; // defineAsyncComponent 반환값이 any로 지정되어 있어 any로 타입지정
  dividerBefore?: boolean; // true면 이 탭 앞에 구분선(|)을 렌더 (서브그룹 구분용, 예: Batch)
};
export type TabPanels<T = string | number | null> = TabPanel<T>[];

export type ViewType = 'view' | 'register' | 'edit';

// ev-tree-grid
export interface TreeGridOption {
  adjust?: boolean;
  showHeader?: boolean;
  rowHeight?: number;
  rowMinHeight?: number;
  columnWidth?: number;
  scrollWidth?: number;
  useFilter?: boolean;
  useCheckbox?: {
    use?: boolean;
    mode?: 'multi' | 'single' | 'each' | 'parentOnly';
    headerCheck?: boolean;
  };
  useSelection?: {
    use?: boolean;
    multiple?: boolean;
    limitCount?: number;
    disableDeselect?: boolean; // EVUI 에서 제공하지 않고 <base-grid> 에서 구현
    disableRootNodeSelect?: boolean; // EVUI 에서 제공하지 않고 <base-grid> 에서 구현
    disableCellClickSelect?: boolean;
  };
  style?: {
    stripe?: boolean;
    border?: 'all' | 'rows' | 'columns' | 'none';
    highlight?: number; // rowIndex
  };
  customContextMenu?: any[];
  expandIcon?: string; // ev-icon
  collapseIcon?: string; // ev-icon
  parentIcon?: string | 'none'; // ev-icon or none
  childIcon?: string | 'none'; // ev-icon or none
  defaultExpandAll?: boolean;
  useSummary?: boolean;
  searchValue?: string;
  useGridSetting?: {
    use?: boolean;
    useDefaultColumnSetting?: boolean;
    customContextMenu?: any[];
    columnMenuText?: string;
    searchText?: string;
    emptyText?: string;
    okBtnText?: string;
    toolbar?: {
      disableMinHeight?: boolean;
      settingIconBottom?: number | string;
    };
    onAfterResetUserEnv?: () => void;
    // 트리 그리드 전용 — true 시 설정 메뉴에 전체 펼치기/접기 항목 자동 추가
    useExpandCollapseMenu?: boolean;
  };
  useExportExcel?: {
    use?: boolean;
    title?: string;
    icon?: string;
  };
  columnMenuText?: {
    hide?: string;
  };
  emptyText?: string;
  /** 원본 rows가 있는데 검색/필터 결과가 0건일 때 표시할 문구. 미지정 시 기본 "검색 결과가 없습니다." — ''로 억제 가능 */
  searchEmptyText?: string;
  maintainScrollOnUpdateRows?: boolean;
  customAscFunction?: Record<string, (a: any, b: any) => number>;
  hiddenColumnMenuItem?: {
    ascending?: boolean;
    descending?: boolean;
  };
}

export interface TreeGridRows<T = GridRawRowDataType> {
  highlight: { backgroundColor: string; index: number; fontColor: string };
  checked: boolean;
  children?: TreeGridRows<T>[];
  data: TreeGridRows<T>;
  expand: boolean;
  hasChild: boolean;
  index: number;
  isFilter: boolean;
  level: number;
  parent?: TreeGridRows<T>;
  selected: boolean;
  show: boolean;
}

export type TimezoneName =
  | 'NONE'
  | 'UTC'
  | 'Asia/Shanghai'
  | 'Asia/Seoul'
  | 'Australia/Darwin'
  | 'Australia/Brisbane'
  | 'Australia/Adelaide'
  | 'Australia/Canberra'
  | 'Asia/Anadyr'
  | 'Pacific/Auckland'
  | 'Pacific/Midway'
  | 'Pacific/Honolulu'
  | 'America/Nome'
  | 'America/Tijuana'
  | 'America/Phoenix'
  | 'America/Chicago'
  | 'America/New_York'
  | 'America/Caracas'
  | 'America/Glace_Bay'
  | 'America/St_Johns'
  | 'America/Argentina/Buenos_Aires'
  | 'America/Noronha'
  | 'Atlantic/Azores'
  | 'Europe/London'
  | 'Europe/Berlin'
  | 'Africa/Cairo'
  | 'Europe/Moscow'
  | 'Asia/Yerevan'
  | 'Asia/Kabul'
  | 'Asia/Tashkent'
  | 'Asia/Kolkata'
  | 'Asia/Kathmandu'
  | 'Asia/Dhaka'
  | 'Asia/Rangoon'
  | 'Asia/Bangkok';

type InValidTimezoneName =
  | 'NONE'
  | 'UTC'
  | 'Pacific/Auckland'
  | 'Australia/Canberra'
  | 'America/Argentina/Buenos_Aires'
  | 'America/Glace_Bay'
  | 'America/St_Johns';
type StandardTimezoneName = Exclude<TimezoneName, InValidTimezoneName>;

type TimezoneFormatting<T> = T extends `${infer A}/${infer B}` ? `${A}_${B}` : T;
type TimezoneFormattingAll<T> = T extends `${infer A}/${infer B}`
  ? TimezoneFormatting<`${A}_${B}`>
  : T;

type StandardTimezoneNameForApiParam = TimezoneFormattingAll<StandardTimezoneName>;

export type TimezoneNameForApiParam =
  | StandardTimezoneNameForApiParam
  | 'America_Argentina_BuenosAires'
  | 'America_GlaceBay'
  | 'America_StJohns';

export type LanguageType = 'NONE' | 'KO' | 'EN' | 'CH' | 'JA';

export type AlertReceptionType = 'alert' | 'email' | 'sms' | 'slack';

export type DBType = (typeof DB_TYPE)[keyof typeof DB_TYPE] | null;
export type MaxgaugeMenuDbType = Exclude<
  NonNullable<DBType>,
  'NONE' | 'mongodb' | 'redis' | 'java' | 'cubrid'
>;

type MaxgaugeSubMenu =
  | 'instanceView'
  | 'sqlAnalysis'
  | 'eventAnalysis' // eventAnalysis는 oracle에만 포함되므로 조건부 타입 사용
  | 'sessionAnalysis'
  | 'performanceAnalysis'
  | 'dataSpaceAnalysis';

type DatabaseType = 'oracle' | 'postgresql' | 'mysql' | 'sqlserver' | 'altibase' | 'tibero';

export type MaxgaugeDatabaseSubMenu = {
  [db in DatabaseType]: db extends 'oracle'
    ? MaxgaugeSubMenu
    : Exclude<MaxgaugeSubMenu, 'eventAnalysis'>;
};

export type AgentType = 'ALL' | 'DB' | 'OS';

export type SessionType = 'Active' | 'Idle' | 'Long' | 'Lock' | 'DEFAULT';

export type WindowSizeType =
  | 'FULL'
  | 'LARGE'
  | 'LARGE_EXPAND'
  | 'EXTRA_LARGE_EXPAND'
  | 'MEDIUM'
  | 'MEDIUM_EXPAND'
  | 'SMALL'
  | 'EXTRA_SMALL';
export type WindowSize = {
  [key in WindowSizeType]: {
    width: string;
    height: string;
    minWidth: string;
    minHeight: string;
  };
};
export type SlideOutSizeType = 'LARGE' | 'MEDIUM' | 'SMALL';

export type BaseColor = 'default' | 'red' | 'blue' | 'yellow';

// single view - session list (Active Backends, Vacuuming Backends)
export type SessionGridRowData = [
  number,
  string,
  string,
  string,
  string,
  string,
  number,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

export interface ApiResultMessage {
  success: string;
  fail: string;
}

// single view - 연계 Window
export interface LinkedWindow {
  fromTime?: string;
  toTime?: string;
  createdTime: number;
  instanceId?: string;
}

export interface Top10ObjectListProps extends LinkedWindow {
  orderBy: OrderBy;
}

export interface MetricChartProps extends LinkedWindow {
  statName: string;
  displayName?: string;
}

export interface Top10SqlListProps extends LinkedWindow {
  sortType: SortType;
}

export interface SessionDetailProps extends LinkedWindow {
  detailInfo: {
    pid?: number;
    queryStart: string;
    threadId?: number;
    digest?: string;
  };
}

export interface SlowQueryDetailProps extends LinkedWindow {
  fromTime: string;
  toTime: string;
  createdTime: number;
  slowQueryDetailInfo: SlowItemV7;
}

export interface Top10SortingListProps extends LinkedWindow {
  orderBy: SortingOrderBy;
}

export interface OracleSessionDetailOptions {
  instanceId?: null | string;
  serialNumber?: null | string;
  sid?: null | string;
}
export interface MultiViewSessionDetailProps extends LinkedWindow {
  sessionReqInfo: OracleSessionDetailOptions;
}

export interface UseWindowPopup<T> {
  windows: Ref<T[]>;
  addWindows: (rowData: any) => void;
  spliceWindows: (index: number) => void;
}

export type LabelType = 'default' | 'primary' | 'ghost' | 'bold';

export interface BorderOption {
  style:
    | 'none'
    | 'hidden'
    | 'dotted'
    | 'dashed'
    | 'solid'
    | 'double'
    | 'groove'
    | 'ridge'
    | 'inset'
    | 'outset';
  width?: number;
}

export type BaseBoxColor = BaseColor | 'dark';
export type BaseBoxSize = 'small' | 'medium' | 'large' | 'full';

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export type IntervalType =
  | 'I10m'
  | 'I10s'
  | 'I15m'
  | 'I15s'
  | 'I1d'
  | 'I1h'
  | 'I1m'
  | 'I20m'
  | 'I2h'
  | 'I30m'
  | 'I3m'
  | 'I5h'
  | 'I5s'
  | 'none';
type FetchCycle = number;
export type FetchInfo = [FetchCycle, IntervalType];
export interface FetchCycleFrame {
  oracle:
    | 'overview'
    | 'dbSessionType'
    | 'dbWaitClass'
    | 'dbTimeModel'
    | 'redo'
    | 'undoTemp'
    | 'dbMetric'
    | 'sessions'
    | 'waitChains'
    | 'sqlSummary'
    | 'event';
}

export interface SearchedItem<LT = LabelType> {
  type?: LT;
  label?: string;
  icon?: string;
  category?: boolean;
  value: string;
  tooltip?: string | string[];
}
export interface CustomError {
  code?: string;
  request?: Record<string, any>;
  response?: Record<string, any>;
  ignoreRequest?: boolean;
}

export interface BulletChartOption {
  useWarningEffect: boolean;
  hideBulletTenLevel: boolean;
  overloadRepeatLimit: number;
  criticalOverloadCriteria: number;
}

export interface BulletChartData {
  time?: number;
  id: number;
  request: number;
  response: number;
  normal: number;
  warning: number;
  critical: number;
}

export interface BulletChartColors {
  lineColor: string;
  labelColor: string;
  totalCountColor: string;
  reqCountColor: string;
  resCountColor: string;
  normalCountColor: string;
  warningCountColor: string;
  criticalCountColor: string;
}

export interface ChartSelectedItem {
  dataIndex: number;
  seriesID: string;
}

export interface ChartSelectedLabel {
  dataIndex: number[];
  targetAxis: 'yAxis' | 'xAxis';
}

export interface ChartSelectedSeries {
  seriesId: string[];
}

export interface StatChartStat {
  id: string;
  name: string;
}

export interface ChartSettings {
  type: 'line' | 'bar';
  fill?: boolean;
  thickness?: number;
  lineWidth?: number;
}

export interface StatChartOption {
  currentStatId?: string;
  chartSettings?: ChartSettings;
}

export interface ContextMenuItems {
  text?: string;
  iconClass?: IconType;
  itemClass?: string;
  disabled?: boolean;
  hidden?: boolean;
  children?: ContextMenuItems[];
  click?: any;
  mode?: WidgetModeType[];
  widgetChartType?: WidgetChartType;
  exceptions?: string[];
}

export type DetailInformationType = Record<
  string,
  { name: string; value: any; rendererType?: 'link' | 'label' | 'status' | 'grid' }
>;

export type FormatSize = 'Bytes' | 'KiB' | 'MiB' | 'GiB' | 'TiB' | 'PiB' | 'EiB' | 'ZiB' | 'YiB';

export type TimePeriodType = MetricV7RequestPeriod;
export type TimeIntervalType = MetricV7RequestInterval;

export type DetailInfoRenderType =
  | 'link'
  | 'status'
  | 'label'
  | 'grid'
  | 'custom'
  | 'text'
  | 'ratio'
  | 'customValue'
  | 'customNameValue'
  | 'grid-fill'
  | 'customRow';

export interface DetailInfoData {
  label: string;
  field: string;
  values: string[];
  renderType?: DetailInfoRenderType;
  hide?: boolean;
  useIconInLabel?: boolean;
  icon?: IconType;
}

/**
 * customValue renderType을 사용하는 DetailInfoData
 * values에 제네릭 타입을 허용하여 복잡한 객체를 전달할 수 있음
 */
export interface DetailInfoDataCustom<T = any> {
  label: string;
  field: string;
  values: T[];
  renderType: 'customValue';
  hide?: boolean;
  useIconInLabel?: boolean;
  icon?: IconType;
}

/**
 * @description
 * DetailInfoData를 그대로 사용해도 되지만, customValue에서 타입 안정성이 필요한 경우
 * DetailInfoDataUnion<T>를 사용하여 명시적으로 타입을 지정할 수 있습니다.
 */
export type DetailInfoDataUnion<T = any> = DetailInfoData | DetailInfoDataCustom<T>;

export type DetailInfoDataListReadOnly = readonly Readonly<DetailInfoData>[];

export interface MetricInfo {
  chartId: string;
  dataId: string;
}

export type MetricRequest = MetricV7Request;
export type CurrentRequest = CurrentV7Request & { targetIds?: string[] };

export interface MetricChartInfo {
  title: string;
  chartId: string;
  chartData: ChartData;
  unit: string;
}

export interface ChildStatusCountInfo {
  icon?: IconType;
  text: string | number;
  name?: string;
}

export interface StatusCountInfo extends ChildStatusCountInfo {
  subText?: string | number;
  children?: ChildStatusCountInfo[];
  title?: string;
}

export interface ConfigGroupInfoTooltipOption {
  preLine?: boolean;
  basePos?: TooltipPosition;
  showTail?: boolean;
  responsivePos?: boolean;
}

// Note: 기존에 model에 삭제됨
interface LogParserKey {
  parserId?: number;
  parserKeys?: string[];
  parserName?: string;
  parserPriority?: number;
}

export interface ConfigGroupInfo<IdType = string> {
  id: IdType;
  name: string;
  description?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'WARNING' | '';
  useDisconnectNotification?: boolean;
  icon?: IconType;
  statusCountList?: StatusCountInfo[];
  configId?: number;
  isDefault?: boolean;
  itemCount?: number;
  leftBottomText?: string;
  rightBottomText?: string;
  disable?: boolean;
  data?: any;
  notification?: NotificationInfo;
  logParserKeys?: LogParserKey[];
  role?: RoleId;
  hideDelete?: boolean;
  hideEdit?: boolean;
  disableDelete?: {
    disable: boolean;
    tooltipMessage?: string;
    tooltipOption?: ConfigGroupInfoTooltipOption;
  };
  applicationGroupConfigId?: number;
  applicationGroupConfigName?: string;
}

export interface OpenSMOptions {
  popupStyle:
    | {
        width: number;
        height: number;
        // 지정 시 window.open features 에 left/top 을 추가해 팝업 위치를 제어한다 (미지정 시 브라우저 기본 위치).
        left?: number;
        top?: number;
      }
    | WindowSizeType;
  isSingle?: boolean;
}

export type RGB = `rgb(${number}, ${number}, ${number})`;
export type RGBA = `rgba(${number}, ${number}, ${number}, ${number})`;
export type HEX = `#${string}`;

export type WidgetApiStatusType = 'fail' | 'success';

export type WidgetChartDataStatusInfo =
  | {
      status: 'fail';
      type: 'api';
      chartDataId: string;
      reason: string;
      errorCode?: `CE${number}`;
      queryId?: string;
    }
  | {
      status: 'fail';
      type: 'custom';
      chartDataId: string;
      reason: string;
      errorCode?: `CE${number}`;
    }
  | {
      status: 'success';
      chartDataId: string;
      info?: string;
      errorCode?: never;
    };

export type TagOperationType = 'OR' | 'AND';
export type TargetTagKind = 'default_tag' | 'user_tag';
export const targetTagTypeSchema = z.enum([
  'attribute',
  'globalVariable',
  'treeGlobalVariable',
  'serviceGroup',
  'tag',
  'exclude_pattern',
  'exclude_and_pattern',
  'pattern',
  'manual',
  'and_pattern',
  'and_manual',
]);
export type TargetTagType = z.infer<typeof targetTagTypeSchema>;

export type TagKindType =
  | 'all'
  | 'business_service'
  | 'database'
  | 'host'
  | 'tag'
  | 'tag_exclude_service'
  | 'exclude_pattern'
  | 'exclude_and_pattern';

export type eventGridRow = [string, string, string, string, string, string];
export type podsGridRow = [string, string, string, string, string, string];

export interface ClickData {
  rowIndex: number;
  chips: string[];
  totalChips: string[];
  evt: any;
}

export const WidgetTriggerPath = [
  'containerInfo',
  'dbInfo',
  'etoeTrace',
  'hostInfo',
  'processInfo',
  'processDetailInfo',
  'namespaceInfo',
  'nodeInfo',
  'podInfo',
  'podEventList',
  'hpaInfo',
  'pvcInfo',
  'wasInfo',
  'wasTransactions',
  'networkDeviceInfo',
  'networkObjectInfo',
  'networkInterface',
  'kafkaClusterInfo',
  'kafkaConsumerGroupList',
  'kafkaBrokerInfo',
  'kafkaTopicInfo',
  'awsInfo',
  'azureInfo',
  'serviceInfo',
  'sqlInfo',
  'sessionInfo',
  'activeSessionInfo',
  'activeTxnList',
  'alertInfo',
  'cubridBrokerInfo',
  'altibaseReplicationInfo',
  'altibaseMemoryTableInfo',
  'webserverInfo',
  'sqlFullTextInfo', // cell page link에서만 사용, db 전용
  'objectInfo', // cell page link에서만 사용, db > mysql, postgresql 전용
  'slowQueryInfo', // 스캐터·히트맵 summary detail에서 mysql slow query detail 연계 시 사용, db 전용
  'filesystemInfo',
  'dbProcessInfo',
  'dbLockInfo',
  'dbSessionInfo',
  'dbClusterInfo',
  'dbSqlList',
  'deploymentInfo',
  'daemonSetInfo',
  'replicaSetInfo',
  'statefulSetInfo',
  'gpuInfo',
  'migInfo',
] as const;
export type WidgetTriggerPathType = (typeof WidgetTriggerPath)[number];

export interface WidgetTriggerInfo {
  seriesId: string;
  targetId: string;
  targetName: string;
  targetAlias?: string;
  triggerHandler?: DataTrigger[];
}

export interface ResDataQuery {
  fromTime: string;
  toTime: string;
  fields: string[];
  interval: IntervalType | string;
  collectInfo: any;
}

export interface LabelItem {
  title: string;
  value: string;
  valueWidth?: string; // width 가 넘어가면 ... 처리 (default: 200px)
}

export interface LabelTextGroupProps {
  labelList: LabelItem[];
  use1stLeftBorder?: boolean;
  bgColor?: string;
  fontSize?: string;
  valueColor?: string;
  titleColor?: string;
  topPadding?: string;
  labelGap?: string;
}

export interface RankAlertListRes {
  collectTime: string | null;
  description: string | null;
  instanceId: string;
  instanceName: string;
  level: number | null;
  name: string | null;
  type: string | null;
  unit: string | null;
  value: string | null;
}

export interface ChartUnitInfo {
  seriesId: string;
  unit: string;
}
export type NonEmptyArray<T> = [T, ...T[]];
export type ElementOf<T> = T extends (infer E)[] ? E : never;

type StartTime = string;
type EndTime = string;
export type DateRange = [StartTime, EndTime] | [];

export type PAToggleDataType = (typeof PA_TOGGLE_DATA_TYPE)[keyof typeof PA_TOGGLE_DATA_TYPE];

export interface DynamicColumnData<T> {
  columns?: string[];
  rowData?: T[];
}

export type DynamicColumn = Omit<CustomColumn, 'caption'> & {
  caption?: string;
};

export interface UserTags {
  tagName?: string;
  tagValues?: string[];
}

export type PlanViewData = { nestedPlanDataList?: string[][]; planDataList?: string[] } | string;

export type HorizontalAlign = 'left' | 'center' | 'right';

export type VerticalAlign = 'top' | 'middle' | 'bottom';

export type DynamicGridOption = {
  excludeFields?: string[];
  includeFields?: string[];
};
export type Seconds = number;

export type TreeGridSortEventParameter = {
  field: string;
  order: 'asc' | 'desc' | 'init';
  column?: CustomColumn;
  columns?: CustomColumn[];
};

export type PaneSize = {
  size: number | string;
  minSize: number | string;
  maxSize: number | string;
};

export interface ZoomIdx {
  startIdx: number;
  endIdx: number;
}

export interface TimeRangeInfo {
  fromTime: string;
  toTime: string;
}
export interface UTCTimeRangeInfo {
  fromTimeUtc: string;
  toTimeUtc: string;
}

type _Combine<T, K extends PropertyKey = T extends unknown ? keyof T : never> = T extends unknown
  ? T & Partial<Record<Exclude<K, keyof T>, never>>
  : never;

export type Combine<T> = { [K in keyof _Combine<T>]: _Combine<T>[K] };

export type DynamicTimeUnit = 'ms' | 's' | 'm' | 'h' | 'd' | 'w' | 'M' | 'y';

export type AlertPlatformType = (typeof ALERT_PLATFORM_TYPES)[keyof typeof ALERT_PLATFORM_TYPES];
export interface ImageFileData {
  id: string;
  fileName: string;
  imgSrc: Blob | string;
  originalSize: number;
}

export interface EvChartClickArgs {
  e: MouseEvent;
  label?: string;
  value?: number;
  seriesId?: string;
  dataIndex?: number;
  acc?: number;
}
